import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noIndex?: boolean;
  schema?: object;
}

const SEO: React.FC<SEOProps> = ({
  title = 'QATE - AI-powered Test Management Platform',
  description = 'Революционная платформа для управления тестированием с поддержкой искусственного интеллекта. Автоматизируйте тестирование, управляйте тест-кейсами и повышайте качество ПО.',
  keywords = 'test management, QA, quality assurance, testing platform, test cases, bug tracking, AI testing, автоматизация тестирования, управление тестами, QA платформа',
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage = '/images/og-image.svg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  noIndex = false,
  schema
}) => {
  const baseUrl = 'https://qate.ru';
  const fullCanonicalUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  const structuredData = schema ? JSON.stringify(schema) : JSON.stringify({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "QATE",
    "description": description,
    "url": baseUrl,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "provider": {
      "@type": "Organization",
      "name": "QATE",
      "url": baseUrl
    }
  });

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="QATE" />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || ogTitle || title} />
      <meta name="twitter:description" content={twitterDescription || ogDescription || description} />
      <meta name="twitter:image" content={twitterImage || fullOgImage} />
      <meta name="twitter:site" content="@QAte_platform" />
      <meta name="twitter:creator" content="@QAte_platform" />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="QATE Team" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="Russian" />
      <meta name="theme-color" content="#1a56db" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {structuredData}
      </script>
    </Helmet>
  );
};

export default SEO; 